import { NetworkBasePopUp } from '../NetworkBasePopUp';
import { GzDialog } from '../../../../ui/gzDialog/component/GzDialog';
import { translateFn } from '../../../../utils/translator.helper';
import { GzMoveToActionWebComponent } from './gzMoveToAction';
import {
  CheckIfGroupNamesCollideRecord,
  CheckIfGroupNamesCollideRequestPayload,
  DeleteComputersRequestRecord,
  DestinationData,
  MoveCompaniesOrCompanyFoldersRequestPayload,
  MoveComputersAcrossCompaniesTaskSpecifics,
  MoveComputersRequestPayload,
  MoveComputersRequestRecord,
  MoveFinishedEventDetail,
  MoveToEvents,
  MoveToPopUpSpecifics,
  MoveToStates,
  UpdateCustomGroupsRequestPayload,
  UpdateCustomGroupsRequestRecord,
  ValidateMovingEndpointsAcrossCompaniesRequestPayload,
  ValidateMovingEndpointsAcrossCompaniesResponse,
} from './types';
import { ButtonColors, ButtonTypes } from '../../../../ui/gzButton/types';
import { FooterButton } from '../NetworkPopUpFooterContentWebComponent';
import { ConsoleRequest, DataProviderEntity, DataProviderType } from '../../../../utils/request';
import { ToastActionButtonMode, ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { TaskSettings } from '../../types';
import { TaskType } from '../../../../tasks/networkTasks/types';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { buildHash } from '@nirvana/shared/navigation';

export class MoveToPopUp extends NetworkBasePopUp<GzMoveToActionWebComponent, MoveToPopUpSpecifics> {
  protected title = translateFn('Move entities');
  protected transformToTaskTarget = true;
  protected extraDialogConfigs: Partial<GzDialog> = {
    isWindow: true,
  };

  protected popUpFooterButtons: Partial<FooterButton>[] = [
    {
      id: 'confirmButton',
      label: translateFn('Move'),
      type: ButtonTypes.FULL,
      color: ButtonColors.PRIMARY,
      disabled: true,
    },
  ];

  protected customEventNameAfterConfirm: null | string = null;

  private alreadyMovedComputers = false;
  private destination: DestinationData;

  private intervalId: NodeJS.Timer;

  protected customSetup(): void {
    this.popUpContent = new GzMoveToActionWebComponent();

    if (!this.data.targets) {
      throw new Error('Targets are required');
    }

    if (!this.data.specifics) {
      throw new Error('Specifics are required');
    }

    this.popUpContent.items = this.data.targets;
    this.popUpContent.states = this.data.specifics.states;
    this.popUpContent.uniqueParentIds = this.data.specifics.uniqueParentIds;
  }

  protected async handleConfirmButtonClicked(event: CustomEvent): Promise<void> {
    if (!this.data.specifics) {
      throw new Error('Specifics are required');
    }

    if (this.data.specifics.isError) {
      return;
    }

    if (this.popUpContent.invalidDestination) {
      this.showCustomToast(translateFn('Cannot move entities to the selected destination.'), ToastSeverity.ERROR);
      return;
    }

    this.destination = this.popUpContent.getDestination();

    try {
      if (this.data.specifics.states[MoveToStates.COMPANY_ENTITIES_SELECTED]) {
        return await this.handleMoveCompanies();
      }

      if (
        this.data.specifics.states[MoveToStates.NETWORK_ENTITIES_SELECTED] ||
        this.data.specifics.states[MoveToStates.CONTAINER_ENTITIES_SELECTED]
      ) {
        return await this.handleMoveNetworkEntities();
      }
    } catch (exception) {
      if (!exception.err || !exception.err.exceptionType) {
        this.showCustomToast(translateFn('Cannot move entities to the selected destination.'), ToastSeverity.ERROR);
        return;
      }

      switch (exception.err.exceptionType) {
        default:
          this.showCustomToast(translateFn('Cannot move entities to the selected destination.'), ToastSeverity.ERROR);
          break;
        case 'Exceptions\\CompanyMigrationException':
          this.showCustomToast(exception.err.message, ToastSeverity.ERROR); // message is already translated in backend
          break;
      }
    }
  }

  private async handleMoveCompanies() {
    if (!this.data.targets) {
      throw new Error('Targets are required to begin move operation');
    }

    const protectedEntitiesIds: Array<string> = this.data.targets.map(target => target._id);

    const requestPayload: MoveCompaniesOrCompanyFoldersRequestPayload = {
      protectedEntitiesIds: protectedEntitiesIds,
      newParentIdOrHash: this.destination.targetId,
    };

    const bgTaskId: string = await ConsoleRequest.createRequest<string>({
      dataProviderType: DataProviderType.CORE,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'Company',
      method: 'moveCompaniesOrCompanyFolders',
      payload: requestPayload,
    });

    this.showCustomToast(translateFn('Entities are currently being moved.'));
    this.customEventNameAfterConfirm = MoveToEvents.MOVE_ENTITIES_FINISHED;

    // perform check every 20 seconds, let user use the interface while waiting
    this.intervalId = setInterval(async () => await this.checkTaskStatus(bgTaskId), 20000);
  }

  private async checkTaskStatus(backgroundTaskId: string): Promise<void> {
    const taskStatus: string | boolean = await ConsoleRequest.createRequest<string | boolean>({
      dataProviderType: DataProviderType.CORE,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'BackgroundTasks',
      method: 'waitForTaskToFinish',
      payload: { backgroundTaskId: backgroundTaskId },
    });

    if (typeof taskStatus === 'boolean') {
      clearInterval(this.intervalId);

      this.showCustomToast(
        taskStatus
          ? translateFn('Entities were moved successfully.')
          : translateFn('Cannot move entities to the selected destination.'),
        taskStatus ? ToastSeverity.SUCCESS : ToastSeverity.ERROR
      );
      this.data.parentElementRef?.dispatchEvent(
        new CustomEvent(MoveToEvents.MOVE_COMPANIES_FINISHED, {
          bubbles: true,
          composed: true,
        })
      );
    }
  }

  private async handleMoveNetworkEntities() {
    if (!this.data.specifics) {
      throw new Error('Specifics are required to begin move operation');
    }

    if (this.data.specifics.states[MoveToStates.TARGETS_ARE_FROM_MSP_COMPANY] && this.destination.isValidMspCompany) {
      return await this.handleMoveNetworkComputersBetweenCompanies();
    }

    // for some reason, someone decided a container host should be treated as a folder....
    if (
      this.data.specifics.states[MoveToStates.NETWORK_FOLDER_SELECTED] ||
      this.data.specifics.states[MoveToStates.CONTAINER_ENTITIES_SELECTED]
    ) {
      await this.handleMoveNetworkFolders();
    }

    if (this.data.specifics.states[MoveToStates.NETWORK_COMPUTERS_SELECTED] && !this.alreadyMovedComputers) {
      await this.handleMoveNetworkComputers();
    }

    this.customEventNameAfterConfirm = MoveToEvents.MOVE_ENTITIES_FINISHED;
    this.showCustomToast(translateFn('Entities successfully moved.'), ToastSeverity.SUCCESS);
  }

  private async handleMoveNetworkComputersBetweenCompanies() {
    if (!this.data.targets) {
      throw new Error('Targets are required to begin move operation');
    }

    const settings: TaskSettings<MoveComputersAcrossCompaniesTaskSpecifics> = this.getTaskSettings();

    const validationResult: ValidateMovingEndpointsAcrossCompaniesResponse =
      await this.validateMovingEndpointsAcrossCompanies();

    const nonEligibleEntities: Array<string> = this.getNonEligibleEntitiesForMoveMSP(validationResult);

    this.customEventNameAfterConfirm = MoveToEvents.MOVE_ENTITIES_FINISHED;

    if (!nonEligibleEntities.length) {
      return await this.createTask({ settings });
    }

    return await this.handleNonEligibleEntitiesMspMove(nonEligibleEntities, settings);
  }

  private getTaskSettings() {
    if (!this.data.targets) {
      throw new Error('Targets are required to begin move operation');
    }

    const settings: TaskSettings<MoveComputersAcrossCompaniesTaskSpecifics> = {
      targets: this.data.targets,
      taskName: this.getTaskName(),
      type: TaskType.TASK_TYPE_MOVE_ENDPOINT_PARENT,
      specifics: {
        destinationGroupId: this.destination.targetId,
      },
    };
    return settings;
  }

  private async validateMovingEndpointsAcrossCompanies(): Promise<ValidateMovingEndpointsAcrossCompaniesResponse> {
    if (!this.data.targets) {
      throw new Error('Targets are required to begin move operation');
    }

    const payload: ValidateMovingEndpointsAcrossCompaniesRequestPayload = {
      protectedEntityIds: this.data.targets.map((target: ProtectedEntity) => target._id),
      destinationProtectedEntityId: this.destination.targetId,
    };

    return await ConsoleRequest.createRequest<ValidateMovingEndpointsAcrossCompaniesResponse>({
      dataProviderType: DataProviderType.ENDPOINTS,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'validateMovingEndpointsAcrossCompanies',
      payload: payload,
    });
  }

  private getNonEligibleEntitiesForMoveMSP(
    validationResult: ValidateMovingEndpointsAcrossCompaniesResponse
  ): Array<string> {
    return Object.keys(validationResult).filter(
      (protectedEntityId: string) => validationResult[protectedEntityId].errors.length
    );
  }

  private async handleNonEligibleEntitiesMspMove(
    nonEligibleEntities: Array<string>,
    taskSettings: TaskSettings<MoveComputersAcrossCompaniesTaskSpecifics>
  ) {
    if (!this.data.targets) {
      throw new Error('Targets are required to begin move operation');
    }

    const eligibleEntities: Array<ProtectedEntity> = this.data.targets.filter(
      (target: ProtectedEntity) => !nonEligibleEntities.includes(target._id)
    );

    if (!eligibleEntities.length) {
      throw new Error('No eligible entities to move');
    }

    taskSettings.targets = eligibleEntities;

    await this.createTaskBase({ settings: taskSettings });

    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: translateFn(
        `The task was created for eligible entities only. Some entities cannot be moved. Check the status here: `
      ),
      hasAction: true,
      actionButtonText: translateFn('Tasks'),
      actionCallback: buildHash('/tasks'),
      severity: ToastSeverity.WARNING,
      actionButtonMode: ToastActionButtonMode.LINK,
    });
  }

  private getTaskName() {
    return translateFn(`Move Endpoint {date}`, { date: new Date().toISOString().split('T')[0] });
  }

  private async handleMoveNetworkFolders() {
    if (!this.data.targets) {
      throw new Error('Targets are required to begin move operation');
    }

    const hasConflictingFolderNames: boolean = await this.checkForConflictingFolderNames();

    // if conflicting names are found, show error toast and return
    if (hasConflictingFolderNames) {
      throw new Error('The destination folder already contains a folder with the same name.');
    }

    // move computers too, that's the order in which console does it
    if (this.data.specifics?.states[MoveToStates.NETWORK_COMPUTERS_SELECTED]) {
      await this.handleMoveNetworkComputers();
      this.alreadyMovedComputers = true;
    }

    await this.moveNetworkFolders();
  }

  private prepareFoldersToCheckNameConflict(): Array<CheckIfGroupNamesCollideRecord> {
    if (!this.data.targets) {
      throw new Error('Targets are required to begin move operation');
    }

    const folders: Array<CheckIfGroupNamesCollideRecord> = [];

    const networkFoldersToMove: Array<string> = this.data.targets
      .filter((target: ProtectedEntity) => target.type === Constants.ProtectedEntities.EPS.TYPE_CUSTOM_GROUP)
      .map((target: ProtectedEntity) => target.name);

    if (networkFoldersToMove.length) {
      folders.push({
        names: networkFoldersToMove,
        type: null,
      });
    }

    const containerFolderIds = this.data.targets
      .filter((target: ProtectedEntity) => target.type === Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER)
      .map((target: ProtectedEntity) => target.name);

    if (containerFolderIds.length) {
      folders.push({
        names: containerFolderIds,
        type: Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER,
      });
    }

    const containerHostFolderIds = this.data.targets
      .filter((target: ProtectedEntity) => target.type === Constants.ProtectedEntities.CONTAINERS.TYPE_HOST)
      .map((target: ProtectedEntity) => target.name);

    if (containerHostFolderIds.length) {
      folders.push({
        names: containerHostFolderIds,
        type: Constants.ProtectedEntities.CONTAINERS.TYPE_HOST,
      });
    }

    return folders;
  }

  private async checkForConflictingFolderNames(): Promise<boolean> {
    const folders: Array<CheckIfGroupNamesCollideRecord> = this.prepareFoldersToCheckNameConflict();

    if (!folders.length) {
      throw new Error('No folders to check for conflicting names but we are moving folders');
    }

    const requestPayload: CheckIfGroupNamesCollideRequestPayload = {
      groups: folders,
      parentId: this.destination.targetId,
    };

    return await ConsoleRequest.createRequest<boolean>({
      dataProviderType: DataProviderType.EPS,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'checkIfGroupNamesCollide',
      payload: requestPayload,
    });
  }

  private prepareFoldersToMove(): Array<UpdateCustomGroupsRequestRecord> {
    if (!this.data.targets) {
      throw new Error('Targets are required to begin move operation');
    }

    return this.data.targets
      .filter(
        (target: ProtectedEntity) =>
          target.type === Constants.ProtectedEntities.EPS.TYPE_CUSTOM_GROUP ||
          target.type === Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER ||
          target.type === Constants.ProtectedEntities.CONTAINERS.TYPE_HOST
      )
      .map((target: ProtectedEntity) => {
        return {
          ...target,
          parentId: this.destination.targetId,
          updateAction: Constants.ProtectedEntities.updateActions.MOVE,
        };
      });
  }

  private async moveNetworkFolders(): Promise<void> {
    const updateCustomGroupsPayload: UpdateCustomGroupsRequestPayload = {
      records: this.prepareFoldersToMove(),
    };

    const response = await ConsoleRequest.createRequest<{ success: boolean; data: Array<ProtectedEntity> }>({
      dataProviderType: DataProviderType.ENDPOINTS,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'updateCustomGroups',
      payload: updateCustomGroupsPayload,
    });

    if (!response.success) {
      throw new Error('Failed to move network folders');
    }

    this.getCustomEventDetail = (): MoveFinishedEventDetail => {
      return {
        oldRecords: updateCustomGroupsPayload.records,
        newRecords: response.data,
      };
    };
  }

  private async handleMoveNetworkComputers() {
    if (!this.data.targets) {
      throw new Error('Targets are required to begin move operation');
    }

    if (this.destination.type === Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER) {
      return await this.handleMoveToDeleted();
    }

    await this.moveComputers();
  }

  private async handleMoveToDeleted(): Promise<void> {
    const computersToDelete: Array<DeleteComputersRequestRecord> = this.prepareComputersToDelete();

    const response: { success: boolean } = await ConsoleRequest.createRequest<{ success: boolean }>({
      dataProviderType: DataProviderType.EPS,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'removeRecordsNirvana',
      payload: {
        records: computersToDelete,
      },
    });

    if (!response.success) {
      this.showCustomToast(translateFn('Could not delete selected entities.'), ToastSeverity.ERROR);
    }

    this.showCustomToast(translateFn('Entities successfully deleted.'), ToastSeverity.SUCCESS);
  }

  private async moveComputers() {
    const computersToMove: Array<MoveComputersRequestRecord> = this.prepareComputersToMove();

    const requestPayload: MoveComputersRequestPayload = {
      records: computersToMove,
    };

    const response: { success: boolean } = await ConsoleRequest.createRequest<{ success: boolean }>({
      dataProviderType: DataProviderType.ENDPOINTS,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'ProtectedEntitiesEPS',
      method: 'moveComputers',
      payload: requestPayload,
    });

    if (!response.success) {
      throw new Error('Failed to move network computers');
    }
  }

  private prepareComputersToMove(): Array<MoveComputersRequestRecord> {
    if (!this.data.targets) {
      throw new Error('Targets are required to begin move operation');
    }

    return this.data.targets
      .filter((target: ProtectedEntity) => target.type === Constants.ProtectedEntities.EPS.TYPE_COMPUTER)
      .map((target: ProtectedEntity) => {
        return {
          ...target,
          parentId: this.destination.targetId,
          updateAction: Constants.ProtectedEntities.updateActions.MOVE,
        };
      });
  }

  private prepareComputersToDelete(): Array<DeleteComputersRequestRecord> {
    if (!this.data.targets) {
      throw new Error('Targets are required to begin move operation');
    }

    return this.data.targets
      .filter((target: ProtectedEntity) => target.type === Constants.ProtectedEntities.EPS.TYPE_COMPUTER)
      .map((target: ProtectedEntity) => {
        return {
          ...target,
          updateAction: 0,
        };
      });
  }
}
